import React, { useState, useEffect, useMemo } from 'react'
import { Select, Spin } from 'antd'
import { ApiRequest } from '../../../services/api/api'

const { Option } = Select

interface PackagingTypeData {
  packagingTypeId: number
  quantity: number
  sleevePkt: number
  packagingCategory: string
}

interface PackagingTypeCascaderProps {
  value?: number | null
  onChange?: (value: number | null) => void
  disabled?: boolean
  style?: React.CSSProperties
  placeholder?: string
}

const CommonFormPackagingTypeCascader: React.FC<PackagingTypeCascaderProps> = (props) => {
  const [packagingData, setPackagingData] = useState<PackagingTypeData[]>([])
  const [loading, setLoading] = useState(true)
  const [cartonQty, setCartonQty] = useState<number | undefined>()
  const [sleeveQty, setSleeveQty] = useState<number | undefined>()

  // 获取所有包装类型数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const result = await ApiRequest({
          url: 'PackagingType/GetAllPackagingType',
          method: 'get',
          isShowSpinner: false
        })
        setPackagingData(result?.data?.data ?? [])
      } catch (err) {
        console.error('Error fetching packaging types:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // 同步内部状态与外部 value
  useEffect(() => {
    const id = props.value
    if (id && packagingData.length > 0) {
      const found = packagingData.find(d => d.packagingTypeId === id)
      if (found) {
        setCartonQty(found.quantity)
        setSleeveQty(found.sleevePkt)
        return
      }
    }
    // 没有匹配项 → 清空选择
    if (!id && !cartonQty && !sleeveQty) {
      // 已经是空状态，不需要操作
    }
  }, [props.value, packagingData])

  // Carton Qty 选项（去重、排序）
  const cartonQtyOptions = useMemo(() => {
    const unique = Array.from(new Set(packagingData.map(d => d.quantity)))
      .filter(v => v > 0)
      .sort((a, b) => a - b)
    return unique.map(v => ({ label: String(v), value: v }))
  }, [packagingData])

  // Sleeve Qty 选项（根据 Carton Qty 过滤）
  const sleeveQtyOptions = useMemo(() => {
    if (!cartonQty) return []
    const unique = Array.from(
      new Set(
        packagingData
          .filter(d => d.quantity === cartonQty && d.sleevePkt > 0 && cartonQty % d.sleevePkt === 0)
          .map(d => d.sleevePkt)
      )
    ).sort((a, b) => a - b)
    return unique.map(v => ({ label: String(v), value: v }))
  }, [packagingData, cartonQty])

  // 预览组合结果
  const previewLabel = useMemo(() => {
    if (!cartonQty || !sleeveQty) {
      return ''
    }
    const match = packagingData.find(d => d.quantity === cartonQty && d.sleevePkt === sleeveQty)
    const category = match?.packagingCategory ?? 'Carton'
    const sleeveCount = cartonQty / sleeveQty
    return `${cartonQty} / ${category} / ${sleeveQty} * ${sleeveCount}`
  }, [cartonQty, sleeveQty, packagingData])

  // 提交选择的包装类型 ID
  const commitSelection = (row: PackagingTypeData | null) => {
    props.onChange?.(row ? row.packagingTypeId : null)
  }

  // 处理 Carton Qty 变化
  const handleCartonQtyChange = (value: number) => {
    setCartonQty(value)
    setSleeveQty(undefined)
    // 不要立即提交 null，等待用户选择 Sleeve Qty
  }

  const handleCartonQtyClear = () => {
    setCartonQty(undefined)
    setSleeveQty(undefined)
    commitSelection(null)
  }

  // 处理 Sleeve Qty 变化
  const handleSleeveQtyChange = (value: number) => {
    setSleeveQty(value)
    if (cartonQty) {
      const match = packagingData.find(d => d.quantity === cartonQty && d.sleevePkt === value)
      commitSelection(match ?? null)
    }
  }

  const handleSleeveQtyClear = () => {
    setSleeveQty(undefined)
    commitSelection(null)
  }

  // 加载状态
  if (loading && packagingData.length === 0) {
    return (
      <div style={{ padding: '8px 0', textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    )
  }

  // 无数据状态
  if (packagingData.length === 0) {
    return (
      <Select
        disabled
        placeholder="暂无可用的包装类型"
        style={{ width: '100%', ...props.style }}
      />
    )
  }

  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    ...props.style
  }

  const previewStyle: React.CSSProperties = {
    marginTop: 8,
    padding: '6px 12px',
    background: '#f6f8ff',
    border: '1px solid #adc6ff',
    borderRadius: 6,
    fontSize: 12,
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  }

  return (
    <div style={wrapperStyle}>
      {/* 拆分行：Carton Qty / Sleeve Qty */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Carton Qty 列 */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textAlign: 'center' }}>
            Carton Size
          </div>
          <Select
            placeholder="Carton Size"
            value={cartonQty}
            onChange={handleCartonQtyChange}
            onClear={handleCartonQtyClear}
            disabled={props.disabled || packagingData.length === 0}
            showSearch
            allowClear
            style={{ width: '100%' }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {cartonQtyOptions.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* 分隔符 */}
        <span style={{ paddingTop: 20, color: '#bbb', fontSize: 16, userSelect: 'none' }}>
          /
        </span>

        {/* Sleeve Qty 列 */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textAlign: 'center' }}>
            Qty per Sleeve
          </div>
          <Select
            placeholder={cartonQty ? 'Qty per Sleeve' : '—'}
            value={sleeveQty}
            onChange={handleSleeveQtyChange}
            onClear={handleSleeveQtyClear}
            disabled={!cartonQty || props.disabled}
            showSearch
            allowClear
            style={{ width: '100%' }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {sleeveQtyOptions.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

      </div>

      {/* 预览：完整包装名称 */}
      {previewLabel && (
        <div style={previewStyle}>
          <span style={{ color: '#888' }}>Packaging Configuration：</span>
          <span style={{ fontWeight: 600, color: '#1677ff' }}>
            {previewLabel}
          </span>
        </div>
      )}
    </div>
  )
}

// 导出便捷函数，用于表单配置
export const commonFormPackagingTypeCascader = (value?: number | null, disabled?: boolean, onChangeFn?: (value: number | null) => void) => {
  return <CommonFormPackagingTypeCascader value={value} disabled={disabled} onChange={onChangeFn} />
}

export default CommonFormPackagingTypeCascader
